<template>
  <div>
    <el-card class="statistics-card" shadow="never" v-loading="loading" element-loading-text="正在统计每日评论变化">
      <div slot="header" class="card-header">
        <div>
          <div class="card-title">每日评论变化</div>
          <div class="card-description">
            {{ date || '-' }} 与 {{ previousDate || '-' }} 的平台评论总量对比，点击平台查看作品明细
          </div>
        </div>
        <el-button type="text" :disabled="loading || !date" @click="load(date, platform)">重新加载</el-button>
      </div>
      <el-alert
        v-if="error"
        class="statistics-alert"
        type="error"
        :closable="false"
        show-icon
        title="每日评论变化加载失败"
        :description="error"
      />
      <el-alert
        v-else-if="hasIncomplete"
        class="statistics-alert"
        type="warning"
        :closable="false"
        show-icon
        title="部分平台暂不能完整对比"
        description="黄色状态会说明具体原因；系统不会把未更新的数据当成 0，也不会误判为评论下降。点击平台可查看具体作品。"
      />
      <el-table
        :data="items"
        empty-text="所选日期暂无作品评论快照"
        class="summary-table"
        :row-style="clickableRow"
        @row-click="openDetails"
      >
        <el-table-column label="平台" min-width="110">
          <template slot-scope="scope"><platform-tag :value="scope.row.platform" /></template>
        </el-table-column>
        <el-table-column label="当日总评论" min-width="120" align="right">
          <template slot-scope="scope">{{ totalText(scope.row, 'current_total') }}</template>
        </el-table-column>
        <el-table-column label="昨日总评论" min-width="120" align="right">
          <template slot-scope="scope">{{ totalText(scope.row, 'previous_total') }}</template>
        </el-table-column>
        <el-table-column label="较昨日" min-width="110" align="right">
          <template slot-scope="scope">
            <el-tooltip v-if="scope.row.net_change === null" :content="statusText(scope.row)" placement="top">
              <span class="muted">暂不可比</span>
            </el-tooltip>
            <span v-else :class="changeClass(scope.row.net_change)">{{ signed(scope.row.net_change) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="变化率" min-width="100" align="right">
          <template slot-scope="scope">{{ rate(scope.row.change_rate) }}</template>
        </el-table-column>
        <el-table-column label="当日已更新作品" min-width="135" align="center">
          <template slot-scope="scope">
            <span :class="scope.row.observed_content_count === scope.row.expected_content_count ? '' : 'warning-text'">
              {{ number(scope.row.observed_content_count) }}/{{ number(scope.row.expected_content_count) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="状态" min-width="180">
          <template slot-scope="scope">
            <el-tooltip :disabled="!statusDescription(scope.row)" :content="statusDescription(scope.row)" placement="top">
              <el-tag size="small" :type="statusType(scope.row)">{{ statusText(scope.row) }}</el-tag>
            </el-tooltip>
            <div v-if="statusNote(scope.row)" class="status-note">{{ statusNote(scope.row) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click.stop="openDetails(scope.row)">{{ detailActionText(scope.row) }}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-drawer
      :title="`${platformName(detailPlatform)} · 每日评论变化明细`"
      :visible.sync="drawerVisible"
      size="78%"
      :before-close="closeDrawer"
    >
      <div class="drawer-body" v-loading="detailsLoading" element-loading-text="正在加载作品变化明细">
        <el-form inline class="detail-filters" @submit.native.prevent>
          <el-form-item label="日期">
            <el-date-picker v-model="detailDate" type="date" value-format="yyyy-MM-dd" placeholder="选择日期" />
          </el-form-item>
          <el-form-item label="平台">
            <el-select v-model="detailPlatform" clearable placeholder="全部平台">
              <el-option v-for="option in platforms" :key="option.value" :label="option.label" :value="option.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="变化类型">
            <el-select v-model="changeType" placeholder="全部变化">
              <el-option label="全部变化" value="all" />
              <el-option label="评论增长" value="increased" />
              <el-option label="评论下降" value="decreased" />
              <el-option label="没有变化" value="unchanged" />
              <el-option label="当日新增作品" value="new_content" />
              <el-option label="当日首次入库" value="baseline_added" />
              <el-option label="当日未更新" value="current_missing" />
              <el-option label="前一日无数据" value="previous_missing" />
              <el-option label="全部数据缺失" value="anomaly" />
            </el-select>
          </el-form-item>
          <el-form-item label="作品">
            <el-input v-model.trim="keyword" clearable placeholder="搜索标题或作品ID" @keyup.enter.native="searchDetails" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="detailsLoading" @click="searchDetails">查询</el-button>
          </el-form-item>
        </el-form>
        <el-alert
          v-if="detailFilterHint"
          class="statistics-alert"
          type="info"
          :closable="false"
          show-icon
          title="已为你定位对应作品"
          :description="detailFilterHint"
        />
        <el-alert
          v-if="detailsError"
          class="statistics-alert"
          type="error"
          :closable="false"
          show-icon
          title="作品变化明细加载失败"
          :description="detailsError"
        />
        <el-table :data="details" empty-text="当前条件下没有作品变化数据" class="details-table">
          <el-table-column label="作品" min-width="260">
            <template slot-scope="scope">
              <a v-if="isURL(scope.row.source_url)" :href="scope.row.source_url" target="_blank" @click.stop>{{ scope.row.title || scope.row.source_id }}</a>
              <span v-else>{{ scope.row.title || scope.row.source_id }}</span>
              <div class="muted source-id">{{ scope.row.source_id }}</div>
            </template>
          </el-table-column>
          <el-table-column label="平台" width="100">
            <template slot-scope="scope"><platform-tag :value="scope.row.platform" /></template>
          </el-table-column>
          <el-table-column label="发布时间" width="150">
            <template slot-scope="scope">{{ formatDate(scope.row.published_at) }}</template>
          </el-table-column>
          <el-table-column label="首次入库时间" width="150">
            <template slot-scope="scope">{{ formatDate(scope.row.first_monitored_at) }}</template>
          </el-table-column>
          <el-table-column label="当日评论" width="100" align="right">
            <template slot-scope="scope">{{ nullableNumber(scope.row.current_count) }}</template>
          </el-table-column>
          <el-table-column label="昨日评论" width="100" align="right">
            <template slot-scope="scope">{{ previousText(scope.row) }}</template>
          </el-table-column>
          <el-table-column label="变化量" width="95" align="right">
            <template slot-scope="scope">
              <span v-if="scope.row.change_value !== null" :class="changeClass(scope.row.change_value)">{{ signed(scope.row.change_value) }}</span>
              <span v-else class="muted">-</span>
            </template>
          </el-table-column>
          <el-table-column label="数据状态" min-width="150">
            <template slot-scope="scope">
              <el-tag size="mini" :type="detailStatusType(scope.row)">{{ detailStatusText(scope.row) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="已采集明细" width="110" align="right">
            <template slot-scope="scope">{{ number(scope.row.collected_comment_count) }}</template>
          </el-table-column>
          <el-table-column label="当日采集时间" width="150">
            <template slot-scope="scope">{{ formatDate(scope.row.current_captured_at) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" @click="viewComments(scope.row)">查看评论</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination">
          <el-pagination
            background
            layout="total, prev, pager, next, jumper"
            :current-page="detailPage"
            :page-size="detailPageSize"
            :total="detailTotal"
            @current-change="changeDetailPage"
          />
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { yuqingApi } from '@/api/yuqing'
import PlatformTag from '../components/PlatformTag.vue'

export default {
  components: { PlatformTag },
  data () {
    return {
      date: '',
      previousDate: '',
      platform: '',
      items: [],
      loading: false,
      error: '',
      drawerVisible: false,
      detailDate: '',
      detailPlatform: '',
      changeType: 'all',
      keyword: '',
      details: [],
      detailsLoading: false,
      detailsError: '',
      detailPage: 1,
      detailPageSize: 20,
      detailTotal: 0,
      platforms: [
        { value: 'douyin', label: '抖音' },
        { value: 'bilibili', label: 'B站' },
        { value: 'xiaohongshu', label: '小红书' },
        { value: 'wechat_channels', label: '视频号' }
      ]
    }
  },
  computed: {
    hasIncomplete () {
      return this.items.some(item => !['complete', 'no_data'].includes(item.calculation_status))
    },
    detailFilterHint () {
      const labels = {
        current_missing: `以下作品缺少 ${this.detailDate || '所选日期'} 的评论数，请检查该作品当天的运营数据更新记录。`,
        previous_missing: '以下作品当日已有数据，但前一日没有评论数，因此暂时无法计算完整变化。',
        baseline_added: '以下作品在所选日期才首次入库。对比发布时间和首次入库时间，可判断是正常新增还是延迟发现。'
      }
      return labels[this.changeType] || ''
    }
  },
  methods: {
    async load (date, platform) {
      if (!date) return
      this.date = date
      this.platform = platform || ''
      this.loading = true
      this.$emit('loading-change', true)
      this.error = ''
      try {
        const result = await yuqingApi.get('creator-content/comment-statistics', {
          date: this.date,
          platform: this.platform || undefined
        })
        this.items = result.items || []
        this.previousDate = result.previous_date || ''
      } catch (error) {
        this.items = []
        this.error = error.message || '请稍后重试'
      } finally {
        this.loading = false
        this.$emit('loading-change', false)
      }
    },
    openDetails (row) {
      if (!row) return
      this.detailDate = this.date
      this.detailPlatform = row.platform
      const focusedTypes = {
        current_incomplete: 'current_missing',
        previous_incomplete: 'previous_missing',
        baseline_changed: 'baseline_added'
      }
      this.changeType = focusedTypes[row.calculation_status] || 'all'
      this.keyword = ''
      this.detailPage = 1
      this.drawerVisible = true
      this.loadDetails()
    },
    closeDrawer (done) {
      if (this.detailsLoading) return this.$message.info('作品明细正在加载，请稍候')
      done()
    },
    searchDetails () {
      if (!this.detailDate) return this.$message.warning('请选择统计日期')
      this.detailPage = 1
      this.loadDetails()
    },
    async loadDetails () {
      if (!this.detailDate) return
      this.detailsLoading = true
      this.detailsError = ''
      try {
        const result = await yuqingApi.get('creator-content/comment-statistics/details', {
          date: this.detailDate,
          platform: this.detailPlatform || undefined,
          change_type: this.changeType,
          keyword: this.keyword || undefined,
          page: this.detailPage,
          page_size: this.detailPageSize
        })
        this.details = result.items || []
        this.detailTotal = Number(result.total || 0)
      } catch (error) {
        this.details = []
        this.detailTotal = 0
        this.detailsError = error.message || '请稍后重试'
      } finally {
        this.detailsLoading = false
      }
    },
    changeDetailPage (page) {
      this.detailPage = page
      this.loadDetails()
    },
    viewComments (row) {
      this.drawerVisible = false
      this.$emit('open-comments', row)
    },
    clickableRow () { return { cursor: 'pointer' } },
    detailActionText (row) {
      const labels = {
        current_incomplete: '查看未更新作品',
        previous_incomplete: '查看缺失作品',
        baseline_changed: '查看首次入库作品'
      }
      return labels[row.calculation_status] || '查看明细'
    },
    platformName (value) {
      const found = this.platforms.find(item => item.value === value)
      return found ? found.label : '全部平台'
    },
    statusText (row) {
      const labels = {
        complete: '数据完整',
        no_data: '暂无数据',
        current_incomplete: `${row.missing_current_count || 0} 篇当日未更新`,
        previous_incomplete: `${row.missing_previous_count || 0} 篇前一日无数据`,
        baseline_changed: `${row.baseline_added_content_count || 0} 篇当日首次入库`
      }
      return labels[row.calculation_status] || '数据待核对'
    },
    statusNote (row) {
      const labels = {
        current_incomplete: '当日数据未齐',
        previous_incomplete: '前一日数据未齐',
        baseline_changed: '前一日尚未采集'
      }
      return labels[row.calculation_status] || ''
    },
    statusDescription (row) {
      if (row.calculation_status === 'current_incomplete') {
        return `共 ${this.number(row.expected_content_count)} 篇作品，${this.number(row.observed_content_count)} 篇有当日数据，暂不计算较前一日变化`
      }
      if (row.calculation_status === 'previous_incomplete') {
        return `${this.number(row.missing_previous_count)} 篇作品缺少前一日数据，暂不计算完整变化`
      }
      if (row.calculation_status === 'baseline_changed') {
        return `${this.number(row.baseline_added_content_count)} 篇作品当日才首次入库，前一日没有运营数据快照`
      }
      return ''
    },
    statusType (row) {
      if (row.calculation_status === 'complete') return 'success'
      if (row.calculation_status === 'no_data') return 'info'
      return 'warning'
    },
    detailStatusType (row) {
      if (['current_missing', 'previous_missing'].includes(row.change_type)) return 'danger'
      if (['baseline_added', 'new_content'].includes(row.change_type)) return 'warning'
      return 'success'
    },
    detailStatusText (row) {
      const labels = {
        current_missing: '当日未更新',
        previous_missing: '前一日无数据',
        baseline_added: '当日首次入库',
        new_content: '当日新发布作品'
      }
      return labels[row.change_type] || '数据完整'
    },
    totalText (row, key) {
      if (row.calculation_status === 'no_data') return '-'
      return this.number(row[key])
    },
    previousText (row) {
      if (row.change_type === 'new_content') return '0'
      return this.nullableNumber(row.previous_count)
    },
    nullableNumber (value) {
      return value === null || value === undefined ? '-' : this.number(value)
    },
    number (value) {
      const number = Number(value || 0)
      return Number.isFinite(number) ? number.toLocaleString('zh-CN') : '-'
    },
    signed (value) {
      const number = Number(value || 0)
      return `${number > 0 ? '+' : ''}${number.toLocaleString('zh-CN')}`
    },
    rate (value) {
      if (value === null || value === undefined) return '-'
      const number = Number(value)
      return `${number > 0 ? '+' : ''}${(number * 100).toFixed(2)}%`
    },
    changeClass (value) {
      const number = Number(value || 0)
      return number > 0 ? 'change-up' : number < 0 ? 'change-down' : ''
    },
    formatDate (value) {
      return value ? String(value).replace('T', ' ').slice(0, 16) : '-'
    },
    isURL (value) {
      try {
        const url = new URL(value)
        return url.protocol === 'http:' || url.protocol === 'https:'
      } catch (error) {
        return false
      }
    }
  }
}
</script>

<style scoped>
.statistics-card { margin-bottom: 16px }
.card-header { display: flex; align-items: center; justify-content: space-between }
.card-title { color: #303133; font-size: 16px; font-weight: 600 }
.card-description { color: #909399; font-size: 12px; margin-top: 5px }
.statistics-alert { margin-bottom: 14px }
.summary-table { width: 100% }
.drawer-body { padding: 0 20px 24px }
.detail-filters { padding: 14px 16px 0; margin-bottom: 14px; background: #f7f8fa; border-radius: 4px }
.details-table { width: 100% }
.muted { color: #909399 }
.source-id { margin-top: 4px; font-size: 12px }
.warning-text { color: #e6a23c; font-weight: 600 }
.status-note { margin-top: 4px; color: #909399; font-size: 12px; line-height: 1.3 }
.change-up { color: #f56c6c; font-weight: 600 }
.change-down { color: #67c23a; font-weight: 600 }
.pagination { display: flex; justify-content: flex-end; margin-top: 16px }
</style>
